import { useState, useRef, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { patchRequestStatus } from '../../shared/api/requests.api'
import { RequestStatusBadge } from '../../shared/ui/Badge'
import type { RequestStatus } from '../../shared/api/types'
import { REQUEST_STATUS_LABELS_RU } from '@crm/shared'
import { RejectionReasonModal } from '../requests/components/RejectionReasonModal'
import { ArchiveRequestModal } from '../requests/components/ArchiveRequestModal'

// Подписи статусов заявки — единый источник в @crm/shared. Раньше здесь была
// своя копия, и SENT_TO_PROCUREMENT подписывался «В закупке», а бейдж рядом —
// «В закупку».
export const STATUS_LABELS: Record<RequestStatus, string> = REQUEST_STATUS_LABELS_RU

// Mirrors backend workflow — for UI hints only (server still validates).
// Актуальные переходы: см. apps/api/src/modules/requests/requests.workflow.ts
const VALID_NEXT: Record<RequestStatus, RequestStatus[]> = {
  DRAFT: ['NEW', 'CLOSED_LOST', 'ARCHIVED'],
  NEW: ['IN_PROGRESS', 'QUOTATION_READY', 'CLOSED_LOST', 'ARCHIVED'],
  SENT_TO_PROCUREMENT: ['IN_PROGRESS', 'QUOTATION_READY', 'CLOSED_LOST', 'ARCHIVED'],
  IN_PROGRESS: ['PARTIALLY_PROCESSED', 'QUOTATION_READY', 'CLOSED_LOST', 'ARCHIVED'],
  PARTIALLY_PROCESSED: ['QUOTATION_READY', 'CLOSED_LOST', 'ARCHIVED'],
  QUOTATION_READY: ['QUOTATION_CREATED', 'QUOTE_SENT', 'CLOSED_LOST', 'ARCHIVED'],
  QUOTATION_CREATED: ['QUOTATION_READY', 'QUOTE_SENT', 'CLOSED_LOST', 'ARCHIVED'],
  QUOTE_SENT: ['QUOTATION_READY', 'CLOSED_WON', 'CLOSED_LOST', 'ARCHIVED'],
  // ARCHIVED из CLOSED_WON убран намеренно: бэк запрещает архивировать заявку
  // «Счёт» (archive.service.ts) — раньше пункт был кликабелен и менеджер
  // получал ошибку уже после клика.
  CLOSED_WON: ['QUOTE_SENT'],
  CLOSED_LOST: ['QUOTE_SENT', 'ARCHIVED'],
  ARCHIVED: [],
}

// В выпадашке смены статуса оставлены только два «ручных» исхода: «Отказ» и
// «В архив». Остальные статусы выставляются автоматикой по ходу работы —
// руками их менять нельзя (по требованию заказчика 16.05.2026).
const STANDARD_STATUSES: RequestStatus[] = ['CLOSED_LOST', 'ARCHIVED']
const CUSTOM_STATUSES_KEY = 'crm_custom_statuses'

export const customStatusKey = (reqId: string) => `crm_cst_${reqId}`

// Используется в Badge / списках заявок для рендера ранее заданных
// произвольных статусов. Возможность задавать новые убрана из выпадашки.
export function getCustomStatuses(): string[] {
  try {
    return JSON.parse(localStorage.getItem(CUSTOM_STATUSES_KEY) ?? '[]')
  } catch {
    return []
  }
}

interface DropdownPos {
  top: number
  left: number
}

export function StatusEditor({
  requestId,
  currentStatus,
  isPartiallyProcessed = false,
  onApplied,
}: {
  requestId: string
  currentStatus: RequestStatus
  isPartiallyProcessed?: boolean
  onApplied: () => void
}) {
  const [open, setOpen] = useState(false)
  const [pos, setPos] = useState<DropdownPos>({ top: 0, left: 0 })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  // Показываем модалку «Причина отказа» при выборе CLOSED_LOST —
  // без обязательного комментария менять статус нельзя.
  const [pendingReject, setPendingReject] = useState(false)
  // «В архив» из выпадашки открывает то же окно с причиной, что и кнопка
  // «В архив» в карточке заявки: причина архивации обязательна.
  const [pendingArchive, setPendingArchive] = useState(false)
  const triggerRef = useRef<HTMLSpanElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const activeCustom = localStorage.getItem(customStatusKey(requestId)) ?? ''

  const openDropdown = useCallback(() => {
    if (!triggerRef.current) return
    const rect = triggerRef.current.getBoundingClientRect()
    // Prefer opening below; if not enough space, open above
    const spaceBelow = window.innerHeight - rect.bottom
    const dropH = 340 // approx max dropdown height
    const top =
      spaceBelow >= dropH ? rect.bottom + window.scrollY + 4 : rect.top + window.scrollY - dropH - 4
    setPos({ top, left: rect.left + window.scrollX })
    setError('')
    setOpen(true)
  }, [])

  // Close on outside click
  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      const target = e.target as Node
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(target) &&
        triggerRef.current &&
        !triggerRef.current.contains(target)
      ) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  // Close on scroll
  useEffect(() => {
    if (!open) return
    const handler = () => setOpen(false)
    window.addEventListener('scroll', handler, true)
    return () => window.removeEventListener('scroll', handler, true)
  }, [open])

  const applyStandard = async (status: RequestStatus) => {
    // CLOSED_LOST требует обязательный комментарий — показываем модалку
    // и выходим из dropdown. Сам patch выполнится в onConfirm модалки.
    if (status === 'CLOSED_LOST') {
      setOpen(false)
      setPendingReject(true)
      return
    }
    // ARCHIVED требует обязательную причину архивации — показываем то же окно,
    // что и кнопка «В архив». Сама архивация идёт через POST /requests/:id/archive.
    if (status === 'ARCHIVED') {
      setOpen(false)
      setPendingArchive(true)
      return
    }
    setLoading(true)
    setError('')
    try {
      await patchRequestStatus(requestId, status)
      localStorage.removeItem(customStatusKey(requestId))
      setOpen(false)
      onApplied()
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        'Недопустимый переход статуса'
      setError(msg)
    } finally {
      setLoading(false)
    }
  }

  const confirmReject = async (reason: string, reasonCode: string) => {
    await patchRequestStatus(requestId, 'CLOSED_LOST', reason, reasonCode)
    localStorage.removeItem(customStatusKey(requestId))
    setPendingReject(false)
    onApplied()
  }

  const validNext = VALID_NEXT[currentStatus] ?? []
  const isCustomActive = !!activeCustom
  const displayLabel = activeCustom || STATUS_LABELS[currentStatus]

  const dropdown = (
    <div
      ref={dropdownRef}
      style={{ position: 'absolute', top: pos.top, left: pos.left, zIndex: 9999 }}
      className="w-60 bg-white border border-gray-200 rounded-xl shadow-xl"
    >
      {/* Только ручные исходы: «Отказ» и «В архив» */}
      <div className="py-1">
        {STANDARD_STATUSES.map((s) => {
          const isCurrent = s === currentStatus
          const isValid = validNext.includes(s)
          const title = isCurrent
            ? 'Заявка уже в этом статусе'
            : !isValid
              ? s === 'ARCHIVED' && currentStatus === 'CLOSED_WON'
                ? 'Заявку «Счёт» нельзя архивировать — сначала откатите из статуса «Счёт»'
                : `Недопустимый переход из статуса «${STATUS_LABELS[currentStatus]}»`
              : undefined
          return (
            <button
              key={s}
              // Невалидные переходы именно ОТКЛЮЧЕНЫ, а не просто серые: иначе
              // менеджер кликает и получает ошибку с бэка.
              disabled={loading || !isValid || isCurrent}
              title={title}
              onClick={() => void applyStandard(s)}
              className={`w-full text-left px-3 py-1.5 text-xs transition-colors flex items-center justify-between gap-2 ${
                isCurrent
                  ? 'font-semibold text-brand-600 bg-brand-50'
                  : isValid
                    ? 'text-gray-800 hover:bg-green-50'
                    : 'text-gray-400 cursor-not-allowed'
              }`}
            >
              <span>{STATUS_LABELS[s]}</span>
              {isValid && !isCurrent && <span className="text-green-500 text-xs shrink-0">→</span>}
            </button>
          )
        })}
      </div>

      {error && (
        <div className="px-3 py-2 border-t border-gray-100 text-xs text-red-500">{error}</div>
      )}
    </div>
  )

  return (
    <>
      <span
        ref={triggerRef}
        onDoubleClick={openDropdown}
        title="Двойной клик для смены статуса"
        className="cursor-pointer select-none"
      >
        {isCustomActive ? (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700 border border-purple-200">
            {displayLabel}
          </span>
        ) : (
          <RequestStatusBadge status={currentStatus} isPartiallyProcessed={isPartiallyProcessed} />
        )}
      </span>

      {open && createPortal(dropdown, document.body)}
      {pendingReject &&
        createPortal(
          <RejectionReasonModal
            onConfirm={confirmReject}
            onCancel={() => setPendingReject(false)}
          />,
          document.body,
        )}
      {pendingArchive &&
        createPortal(
          <ArchiveRequestModal
            requestId={requestId}
            onClose={() => setPendingArchive(false)}
            onArchived={() => {
              setPendingArchive(false)
              localStorage.removeItem(customStatusKey(requestId))
              onApplied()
            }}
          />,
          document.body,
        )}
    </>
  )
}
